// ─────────────────────────────────────────────────────────────────────────────
// UploadDocumentDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dialog for uploading a knowledge-base document.
// Responsibilities:
//   - File drag-and-drop / browse
//   - Category autocomplete (free-text or preset)
//   - Tags input (comma-separated)
//   - Context for AI (internal notes)
//   - Customers Scope (all / by company / by group)
//   - Product Scope (all / by brand, category, label / manual)
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Action, Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, Subject, startWith, take, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { CommonService } from 'src/app/shared/common.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { SelectableProductsComponent } from 'src/app/shared/components/selectable-products/selectable-products.component';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';

import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { ICustomer } from 'src/app/features/customers/models/customers.models';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { IProduct } from 'src/app/features/produits/models/products.models';

import { FetchCompaniesGroups } from 'src/app/features/companies-groups/store/companies-groups.actions';
import { FetchAllCustomers } from 'src/app/features/customers/store/customers.actions';
import { FetchAllBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchAllCategories } from 'src/app/features/categories/store/categories.actions';
import { FetchAllTags } from 'src/app/features/tags/store/tags.actions';

import * as KbActions from '../../../store/knowledge-base.actions';

import { atLeastOneOfMultipleValidator, atLeastOneValidator } from 'src/app/shared/validators/fields-validators';

import { IKbDocument, IKbUpdateRequest, IKbUploadRequest, KbDocumentType } from '../../../models/knowledge-base.models';

import { Actions, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';

export interface UploadDocumentDialogData {
  document: IKbDocument;
}

@Component({
  selector: 'app-upload-document-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
    UiModule,
    SharedDialogComponent,
    SelectableProductsComponent,
  ],
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
})
export class UploadDocumentDialogComponent implements OnInit, OnDestroy {
  // ── Mode ──────────────────────────────────────────────────────────────────

  isEditMode = false;
  editDocumentId = '';
  existingFileName = '';

  // ── File ──────────────────────────────────────────────────────────────────

  selectedFile: File | null = null;
  isDragOver = false;
  // .msg / .eml / .zip are not accepted here: price e-mails go through "Import
  // prices", which extracts each line with the file and row it came from. A
  // .msg dropped here would be indexed as prose and never become an
  // observation.
  acceptedTypes = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json';

  // ── Form ──────────────────────────────────────────────────────────────────

  uploadForm!: FormGroup;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  tags: string[] = [];

  // ── Document type ─────────────────────────────────────────────────────────

  // Price sources are not offered here: 'price_list' goes through "Import
  // prices" (which extracts line-level observations) and 'order_history' is
  // live Odoo data, never a knowledge-base document. Both stay in the *filter*
  // and in the document list, so existing documents remain visible -- only the
  // upload choice is narrowed.
  //
  // Without the pricing addon that door does not exist, so 'price_list' stays
  // uploadable rather than being taken away with nothing in its place.
  private static readonly UPLOAD_EXCLUDED_TYPES: readonly KbDocumentType[] = [
    KbDocumentType.PRICE_LIST,
    KbDocumentType.ORDER_HISTORY,
  ];

  pricingEnabled = false;
  documentTypeOptions: SelectOption[] = [];

  // ── Scope collapse state ──────────────────────────────────────────────────

  customersScopeExpanded = false;
  productScopeExpanded = false;

  // ── Audience scope ────────────────────────────────────────────────────────

  audienceOptions: SelectOption[] = [
    { label: this.translateService.instant('knowledge_base.upload.audience_all'), value: 'all' },
    { label: this.translateService.instant('knowledge_base.upload.audience_by_company'), value: 'by_company' },
    { label: this.translateService.instant('knowledge_base.upload.audience_by_group'), value: 'by_group' },
  ];

  // Companies (by_company scope)
  customers$: Observable<ICustomer[]> = this.store.select((state) =>
    [...state.customers.allCustomers].sort((a, b) => a.name.localeCompare(b.name)),
  );
  customers: ICustomer[] = [];
  filteredCustomers!: Observable<ICustomer[]>;
  selectedCustomers: ICustomer[] = [];
  customerControl = new FormControl('');

  // Groups (by_group scope)
  companiesGroups$: Observable<ICompaniesGroups[]> = this.store.select((state) =>
    [...state.companiesGroups.AllCompaniesGroups].sort((a, b) => a.name.localeCompare(b.name)),
  );
  companiesGroups: ICompaniesGroups[] = [];
  filteredCompaniesGroups!: Observable<ICompaniesGroups[]>;
  selectedGroups: ICompaniesGroups[] = [];
  groupControl = new FormControl('');

  // ── Product scope ─────────────────────────────────────────────────────────

  productScopeOptions: SelectOption[] = [
    { label: this.translateService.instant('knowledge_base.upload.product_scope_all'), value: 'all' },
    { label: this.translateService.instant('knowledge_base.upload.product_scope_by_brand_category_label'), value: 'by_brand_category_label' },
    { label: this.translateService.instant('knowledge_base.upload.product_scope_manual'), value: 'manual' },
  ];

  brands$: Observable<IBrand[]> = this.store.select((state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name)));
  brands: IBrand[] = [];
  filteredBrands!: Observable<IBrand[]>;
  selectedBrands: IBrand[] = [];
  brandControl = new FormControl('');

  categories$: Observable<ICategory[]> = this.store.select((state) =>
    [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)),
  );
  storeCategories: ICategory[] = [];
  filteredStoreCategories!: Observable<ICategory[]>;
  selectedStoreCategories: ICategory[] = [];
  storeCategoryControl = new FormControl('');

  labels$: Observable<ITag[]> = this.store.select((state) => [...state.tags.allTags].sort((a, b) => a.name.localeCompare(b.name)));
  labels: ITag[] = [];
  filteredLabels!: Observable<ITag[]>;
  selectedLabels: ITag[] = [];
  labelControl = new FormControl('');

  selectedProducts: IProduct[] = [];
  prefillProductIds: number[] = [];

  // ── Loading state ───────────────────────────────────────────────────────
  uploadLoading = false;

  // ── Pending IDs (resolved asynchronously after store data loads) ────────

  private pendingGroupIds: number[] = [];
  private pendingCompanyIds: number[] = [];
  private pendingBrandIds: number[] = [];
  private pendingCategoryIds: number[] = [];
  private pendingLabelIds: number[] = [];

  // ── Validators ────────────────────────────────────────────────────────────

  private audienceValidator = atLeastOneOfMultipleValidator('audience', ['groups', 'companies']);
  private productScopeValidator = atLeastOneOfMultipleValidator('product_scope', ['brands', 'categories', 'labels']);
  private manualProductValidator = atLeastOneValidator();
  activeValidators: ValidatorFn[] = [];

  // ── Refs ──────────────────────────────────────────────────────────────────

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<UploadDocumentDialogComponent>,
    private store: Store<AppState>,
    private actions$: Actions<Action>,
    private commonService: CommonService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: UploadDocumentDialogData,
  ) {}

  ngOnInit(): void {
    this.featureFlagService.loadFeatures().pipe(takeUntil(this.destroy$)).subscribe();

    this.featureFlagService.pricingEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.pricingEnabled = enabled;
        this.documentTypeOptions = this.buildDocumentTypeOptions();
      });

    this.uploadForm = new FormGroup({
      documentTypes: new FormControl<KbDocumentType[]>([KbDocumentType.GENERAL]),
      tags: new FormControl([]),
      internalNotes: new FormControl(''),
      audience: new FormControl('all'),
      companies: new FormControl([]),
      groups: new FormControl([]),
      product_scope: new FormControl('all'),
      brands: new FormControl([]),
      categories: new FormControl([]),
      labels: new FormControl([]),
      products: new FormControl([]),
    });

    if (this.dialogData?.document) {
      this.prefillFromDocument(this.dialogData.document);
    }

    this.fetchSelectsData();
    this.resolvePendingSelections();

    this.actions$
      .pipe(
        ofType(KbActions.UploadDocumentSuccess, KbActions.UpdateDocumentSuccess, KbActions.UploadDocumentError, KbActions.UpdateDocumentError),
        takeUntil(this.destroy$),
      )
      .subscribe((action: TypedAction<string> & { error?: string }) => {
        if (action.type === KbActions.UploadDocumentSuccess.type || action.type === KbActions.UpdateDocumentSuccess.type) {
          this.dialogRef.close(true);
        }

        this.uploadLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildDocumentTypeOptions(): SelectOption[] {
    const excluded = this.pricingEnabled
      ? UploadDocumentDialogComponent.UPLOAD_EXCLUDED_TYPES
      : [KbDocumentType.ORDER_HISTORY];
    return Object.values(KbDocumentType)
      .filter((type) => !excluded.includes(type))
      .map((type) => ({
        label: this.translateService.instant(`knowledge_base.filter_type.${type}`),
        value: type,
      }));
  }

  // ── File handling ─────────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  onBrowse(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // ── Tags ──────────────────────────────────────────────────────────────────

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && !this.tags.includes(value)) {
      this.tags.push(value);
      this.uploadForm.get('tags')?.setValue([...this.tags]);
    }
    event.chipInput?.clear();
  }

  removeTag(tag: string): void {
    this.tags = this.tags.filter((t) => t !== tag);
    this.uploadForm.get('tags')?.setValue([...this.tags]);
  }

  // ── Audience scope ────────────────────────────────────────────────────────

  onAudienceChange(_event: any): void {
    const audience = this.uploadForm.get('audience')?.value;
    this.audienceScopeCleanUp();

    if (audience === 'by_company' || audience === 'by_group') {
      if (!this.activeValidators.includes(this.audienceValidator)) {
        this.activeValidators.push(this.audienceValidator);
      }
    } else {
      this.activeValidators = this.activeValidators.filter(v => v !== this.audienceValidator);
    }

    this.uploadForm.setValidators(this.activeValidators);
    this.uploadForm.updateValueAndValidity();
  }

  // ── Product scope ─────────────────────────────────────────────────────────

  onProductScopeChange(_event: any): void {
    const productScope = this.uploadForm.get('product_scope')?.value;
    this.handleScopeChange('product_scope', productScope, this.productScopeValidator, 'products', this.manualProductValidator, () =>
      this.productScopeCleanUp(),
    );
  }

  onSelectedProductsChange(products: IProduct[]): void {
    this.selectedProducts = products;
    const productIds = products.map((p) => p.id);
    this.uploadForm.get('products')?.setValue(productIds);
    if (!productIds.length) {
      this.uploadForm.get('products')?.markAsTouched();
      this.uploadForm.get('products')?.markAsDirty();
    }
  }

  // ── Company multi-select (by_company scope) ───────────────────────────────

  selectCustomer(customer: ICustomer): void {
    this.selectItem(customer, this.selectedCustomers, this.customerControl, 'companies');
  }

  removeCustomer(customer: ICustomer): void {
    this.removeItem(customer, this.selectedCustomers, this.customerControl, 'companies');
  }

  // ── Group multi-select (by_group scope) ───────────────────────────────────

  selectGroup(group: ICompaniesGroups): void {
    this.selectItem(group, this.selectedGroups, this.groupControl, 'groups');
  }

  removeGroup(group: ICompaniesGroups): void {
    this.removeItem(group, this.selectedGroups, this.groupControl, 'groups');
  }

  addGroupFromInput(event: MatChipInputEvent): void {
    this.addItemFromInput(event, this.companiesGroups, this.selectedGroups, this.groupControl, 'groups');
  }

  // ── Brands ────────────────────────────────────────────────────────────────

  selectBrand(brand: IBrand): void {
    this.selectItem(brand, this.selectedBrands, this.brandControl, 'brands');
  }

  removeBrand(brand: IBrand): void {
    this.removeItem(brand, this.selectedBrands, this.brandControl, 'brands');
  }

  addBrandFromInput(event: MatChipInputEvent): void {
    this.addItemFromInput(event, this.brands, this.selectedBrands, this.brandControl, 'brands');
  }

  // ── Categories (product scope) ────────────────────────────────────────────

  selectStoreCategory(category: ICategory): void {
    this.selectItem(category, this.selectedStoreCategories, this.storeCategoryControl, 'categories');
  }

  removeStoreCategory(category: ICategory): void {
    this.removeItem(category, this.selectedStoreCategories, this.storeCategoryControl, 'categories');
  }

  addStoreCategoryFromInput(event: MatChipInputEvent): void {
    this.addItemFromInput(event, this.storeCategories, this.selectedStoreCategories, this.storeCategoryControl, 'categories');
  }

  // ── Labels ────────────────────────────────────────────────────────────────

  selectLabel(label: ITag): void {
    this.selectItem(label, this.selectedLabels, this.labelControl, 'labels');
  }

  removeLabel(label: ITag): void {
    this.removeItem(label, this.selectedLabels, this.labelControl, 'labels');
  }

  addLabelFromInput(event: MatChipInputEvent): void {
    this.addItemFromInput(event, this.labels, this.selectedLabels, this.labelControl, 'labels');
  }

  // ── Validation getters ────────────────────────────────────────────────────

  get audienceScopeError(): boolean {
    const hasError = this.uploadForm.hasError('AtLeastOneOfMultiple_audience');
    const audienceControl = this.uploadForm.get('audience');
    return (
      hasError &&
      (audienceControl?.dirty ||
        audienceControl?.touched ||
        this.customerControl?.dirty ||
        this.customerControl?.touched ||
        this.groupControl?.dirty ||
        this.groupControl?.touched)
    );
  }

  get productScopeHaveAtleastOneBrandCategoryLabelError(): boolean {
    return (
      this.uploadForm.hasError('AtLeastOneOfMultiple_product_scope') &&
      (this.brandControl?.dirty ||
        this.brandControl?.touched ||
        this.storeCategoryControl?.dirty ||
        this.storeCategoryControl?.touched ||
        this.labelControl?.dirty ||
        this.labelControl?.touched)
    );
  }

  // ── Dialog actions ────────────────────────────────────────────────────────

  get canConfirm(): boolean {
    return this.isEditMode || !!this.selectedFile;
  }

  onConfirm(): void {
    const formVal = this.uploadForm.getRawValue();

    this.uploadLoading = true;
    // Primary type (= document_types[0]) is the ATO folder; the array carries every selected category.
    const documentTypes: KbDocumentType[] = formVal.documentTypes?.length ? formVal.documentTypes : [KbDocumentType.GENERAL];
    const metadata: IKbUploadRequest['metadata'] = {
      document_type: documentTypes[0],
      document_types: documentTypes,
      tags: formVal.tags?.length ? formVal.tags : undefined,
      user_comment: formVal.internalNotes || undefined,
    };

    if (formVal.audience !== 'all') {
      metadata.customer_scope = this.buildCustomerScope(formVal.audience, formVal.companies ?? [], formVal.groups ?? []);
    }

    if (formVal.product_scope !== 'all') {
      metadata.product_scope = this.buildProductScope(formVal.product_scope, formVal.brands, formVal.categories, formVal.labels, formVal.products);
    }

    if (this.isEditMode) {
      const request: IKbUpdateRequest = {
        documentId: this.editDocumentId,
        metadata,
        ...(this.selectedFile ? { file: this.selectedFile } : {}),
      };
      this.store.dispatch(KbActions.UpdateDocument({ request }));
    } else {
      if (!this.selectedFile) return;
      const request: IKbUploadRequest = { file: this.selectedFile, metadata };
      this.store.dispatch(KbActions.UploadDocument({ request }));
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private buildCustomerScope(
    scope: string,
    companyIds: number[],
    groupIds: number[],
  ): IKbUploadRequest['metadata']['customer_scope'] {
    const result: IKbUploadRequest['metadata']['customer_scope'] = {
      scope: scope as 'all' | 'by_company' | 'by_group',
    };
    if (companyIds.length) result.company_ids = companyIds;
    if (groupIds.length) result.group_ids = groupIds;
    return result;
  }

  private buildProductScope(
    scope: string,
    brandIds?: number[],
    categoryIds?: number[],
    labelIds?: number[],
    productIds?: number[],
  ): IKbUploadRequest['metadata']['product_scope'] {
    const result: IKbUploadRequest['metadata']['product_scope'] = {
      scope: scope as 'all' | 'by_brand_category_label' | 'manual',
    };
    if (brandIds?.length) result.brand_ids = brandIds;
    if (categoryIds?.length) result.category_ids = categoryIds;
    if (labelIds?.length) result.label_ids = labelIds;
    if (productIds?.length) result.product_ids = productIds;
    return result;
  }

  private fetchSelectsData(): void {
    this.store.dispatch(FetchAllCustomers());
    this.store.dispatch(FetchCompaniesGroups({}));
    this.store.dispatch(FetchAllBrands({}));
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchAllTags({}));

    // Audience autocompletes
    this.filteredCustomers = combineLatest([
      this.customerControl.valueChanges.pipe(startWith('')),
      this.customers$.pipe(takeUntil(this.destroy$)),
    ]).pipe(
      map(([value, customers]) => {
        this.customers = customers.filter(c => c.name?.trim());
        return this.filterSelectableItems(value || '', this.customers, this.selectedCustomers);
      }),
    );

    this.filteredCompaniesGroups = combineLatest([
      this.groupControl.valueChanges.pipe(startWith('')),
      this.companiesGroups$.pipe(takeUntil(this.destroy$)),
    ]).pipe(
      map(([value, groups]) => {
        this.companiesGroups = groups.filter((g) => g.name?.trim());
        return this.filterSelectableItems(value || '', this.companiesGroups, this.selectedGroups);
      }),
    );

    // Product scope autocompletes
    this.filteredBrands = combineLatest([this.brandControl.valueChanges.pipe(startWith('')), this.brands$.pipe(takeUntil(this.destroy$))]).pipe(
      map(([value, brands]) => {
        this.brands = brands.filter((b) => b.name?.trim());
        return this.filterSelectableItems(value || '', this.brands, this.selectedBrands);
      }),
    );

    this.filteredStoreCategories = combineLatest([
      this.storeCategoryControl.valueChanges.pipe(startWith('')),
      this.categories$.pipe(takeUntil(this.destroy$)),
    ]).pipe(
      map(([value, categories]) => {
        this.storeCategories = categories.filter((c) => c.name?.trim());
        return this.filterSelectableItems(value || '', this.storeCategories, this.selectedStoreCategories);
      }),
    );

    this.filteredLabels = combineLatest([this.labelControl.valueChanges.pipe(startWith('')), this.labels$.pipe(takeUntil(this.destroy$))]).pipe(
      map(([value, labels]) => {
        this.labels = labels.filter((l) => l.name?.trim());
        return this.filterSelectableItems(value || '', this.labels, this.selectedLabels);
      }),
    );
  }

  private filterSelectableItems<T extends { id: number; name: string }>(value: string | T, items: T[], selected: T[]): T[] {
    const available = items.filter((item) => !selected.some((sel) => sel.id === item.id));
    if (typeof value === 'string') {
      const filter = value.toLowerCase();
      return available.filter((item) => item.name.toLowerCase().includes(filter));
    }
    return available;
  }

  private selectItem<T extends { id: number }>(item: T, selectedItems: T[], control: FormControl, formFieldName: string): void {
    if (!selectedItems.some((s) => s.id === item.id)) {
      selectedItems.push(item);
    }
    control.setValue('');
    this.uploadForm.get(formFieldName)?.setValue(selectedItems.map((s) => s.id));
  }

  private removeItem<T extends { id: number }>(item: T, selectedItems: T[], control: FormControl, formFieldName: string): void {
    const filtered = selectedItems.filter((s) => s.id !== item.id);
    selectedItems.splice(0, selectedItems.length, ...filtered);
    control.setValue(control.value);
    this.uploadForm.get(formFieldName)?.setValue(selectedItems.map((s) => s.id));
  }

  private addItemFromInput<T extends { id: number; name: string }>(
    event: MatChipInputEvent,
    available: T[],
    selected: T[],
    control: FormControl,
    formFieldName: string,
  ): void {
    const value = event.value?.trim();
    if (value) {
      const match = available.find((item) => item.name.toLowerCase() === value.toLowerCase());
      if (match && !selected.some((s) => s.id === match.id)) {
        selected.push(match);
      }
    }
    if (event.input) {
      event.input.value = '';
    }
    control.setValue('');
    this.uploadForm.get(formFieldName)?.setValue(selected.map((s) => s.id));
  }

  private handleScopeChange(
    scopeKey: 'product_scope',
    scopeValue: string,
    groupedValidator: ValidatorFn,
    manualControlKey: string,
    manualValidator: ValidatorFn,
    cleanupFn: () => void,
  ): void {
    cleanupFn();

    if (scopeKey === 'product_scope' && scopeValue === 'by_brand_category_label') {
      if (!this.activeValidators.includes(groupedValidator)) {
        this.activeValidators.push(groupedValidator);
      }
    } else {
      this.activeValidators = this.activeValidators.filter((v) => v !== groupedValidator);
    }

    const manualControl = this.uploadForm.get(manualControlKey);
    if (scopeValue === 'manual') {
      manualControl?.addValidators(manualValidator);
      manualControl?.updateValueAndValidity();
    } else {
      manualControl?.removeValidators(manualValidator);
      manualControl?.updateValueAndValidity();
    }

    this.uploadForm.setValidators(this.activeValidators);
    this.uploadForm.updateValueAndValidity();
  }

  private audienceScopeCleanUp(): void {
    this.selectedCustomers = [];
    this.selectedGroups = [];
    this.customerControl.setValue('');
    this.groupControl.setValue('');
    this.uploadForm.get('companies')?.setValue([]);
    this.uploadForm.get('groups')?.setValue([]);
  }

  private prefillFromDocument(doc: IKbDocument): void {
    this.isEditMode = true;
    this.editDocumentId = doc.id;
    this.existingFileName = doc.fileName;

    this.uploadForm.patchValue({
      documentTypes: doc.types?.length ? doc.types : [doc.type || KbDocumentType.GENERAL],
      internalNotes: doc.userComment || '',
    });

    if (doc.tags?.length) {
      this.tags = [...doc.tags];
      this.uploadForm.get('tags')?.setValue([...doc.tags]);
    }

    this.prefillCustomerScope(doc);
    this.prefillProductScope(doc);
  }

  private prefillCustomerScope(doc: IKbDocument): void {
    if (!doc.customerScope) return;
    const { scope } = doc.customerScope;

    this.uploadForm.get('audience')?.setValue(scope);
    this.customersScopeExpanded = true;

    if (scope === 'by_company') {
      const ids = doc.customerScope.companyIds ?? [];
      if (ids.length) {
        this.pendingCompanyIds = [...ids];
        this.uploadForm.get('companies')?.setValue([...ids]);
      }
    } else if (scope === 'by_group') {
      const ids = doc.customerScope.groupIds ?? [];
      if (ids.length) {
        this.pendingGroupIds = [...ids];
        this.uploadForm.get('groups')?.setValue([...ids]);
      }
    }

    if (scope === 'by_company' || scope === 'by_group') {
      if (!this.activeValidators.includes(this.audienceValidator)) {
        this.activeValidators.push(this.audienceValidator);
      }
      this.uploadForm.setValidators(this.activeValidators);
      this.uploadForm.updateValueAndValidity();
    }
  }

  private prefillProductScope(doc: IKbDocument): void {
    if (!doc.productScope) return;
    const { scope } = doc.productScope;

    this.uploadForm.get('product_scope')?.setValue(scope);
    this.productScopeExpanded = true;

    if (scope === 'by_brand_category_label') {
      this.pendingBrandIds = doc.productScope.brandIds;
      this.pendingCategoryIds = doc.productScope.categoryIds;
      this.pendingLabelIds = doc.productScope.labelIds;
      this.uploadForm.get('brands')?.setValue([...this.pendingBrandIds]);
      this.uploadForm.get('categories')?.setValue([...this.pendingCategoryIds]);
      this.uploadForm.get('labels')?.setValue([...this.pendingLabelIds]);
    } else if (scope === 'manual') {
      this.prefillProductIds = [...doc.productScope.productIds];
      this.uploadForm.get('products')?.setValue([...this.prefillProductIds]);
    }

    this.handleScopeChange('product_scope', scope, this.productScopeValidator, 'products', this.manualProductValidator, () => {});
  }

  /** Resolves pending IDs into full objects once reference data loads from the store. */
  private resolvePendingSelections(): void {
    if (this.pendingCompanyIds.length) {
      this.customers$.pipe(
        filter(items => items.length > 0), take(1), takeUntil(this.destroy$),
      ).subscribe(customers => {
        this.selectedCustomers = customers.filter(c => this.pendingCompanyIds.includes(c.id));
        this.uploadForm.get('companies')?.setValue(this.selectedCustomers.map(c => c.id));
        this.pendingCompanyIds = [];
      });
    }

    if (this.pendingGroupIds.length) {
      this.companiesGroups$
        .pipe(
          filter((items) => items.length > 0),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((groups) => {
          this.selectedGroups = groups.filter((g) => this.pendingGroupIds.includes(g.id));
          this.uploadForm.get('groups')?.setValue(this.selectedGroups.map((g) => g.id));
          this.pendingGroupIds = [];
        });
    }

    if (this.pendingBrandIds.length) {
      this.brands$
        .pipe(
          filter((items) => items.length > 0),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((brands) => {
          this.selectedBrands = brands.filter((b) => this.pendingBrandIds.includes(b.id));
          this.uploadForm.get('brands')?.setValue(this.selectedBrands.map((b) => b.id));
          this.pendingBrandIds = [];
        });
    }

    if (this.pendingCategoryIds.length) {
      this.categories$
        .pipe(
          filter((items) => items.length > 0),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((categories) => {
          this.selectedStoreCategories = categories.filter((c) => this.pendingCategoryIds.includes(c.id));
          this.uploadForm.get('categories')?.setValue(this.selectedStoreCategories.map((c) => c.id));
          this.pendingCategoryIds = [];
        });
    }

    if (this.pendingLabelIds.length) {
      this.labels$
        .pipe(
          filter((items) => items.length > 0),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((labels) => {
          this.selectedLabels = labels.filter((l) => this.pendingLabelIds.includes(l.id));
          this.uploadForm.get('labels')?.setValue(this.selectedLabels.map((l) => l.id));
          this.pendingLabelIds = [];
        });
    }
  }

  private productScopeCleanUp(): void {
    this.selectedBrands = [];
    this.selectedStoreCategories = [];
    this.selectedLabels = [];
    this.selectedProducts = [];
    this.brandControl.setValue('');
    this.storeCategoryControl.setValue('');
    this.labelControl.setValue('');
    this.uploadForm.get('brands')?.setValue([]);
    this.uploadForm.get('categories')?.setValue([]);
    this.uploadForm.get('labels')?.setValue([]);
    this.uploadForm.get('products')?.setValue([]);
  }
}
